import { Component, Vue } from 'vue-property-decorator';
import { isEmpty, isNull } from 'lodash';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { NOTIFICATION, REWARD_PERIOD_STATUSES, REWARD_REQUEST_STATUSES, DATE_FORMAT } from '@/constants';
import { displayPrizeType, formatCurrency } from '@/helpers';
import {
  RewardPeriodDTO,
  RewardRequestDisplayDTO,
  RewardRequestDTO,
  SelectionOptions,
  TableHeaderColumns
} from '@/models';
import {
  fetchRewardRequestByPeriodId,
  sortArrayByDateField,
  sortArrByRewardRequestStatus,
  sortArrByUserDTOField,
  sortArrByNumberField,
  updateRewardRequest,
  sortArrByTextField
} from '@/services';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import EditRewardItemActualPriceDialog from '@/components/pages/reward-period-detail/edit-reward-item-actual-price-dialog/EditRewardItemActualPriceDialog.vue';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import StatusButtons from '../status-buttons/StatusButtons.vue';

interface DisplayActualPrice {
  class: string;
  text: string;
}

@Component({
  components: {
    ConfirmationDialog,
    EditRewardItemActualPriceDialog,
    FilterDropdown,
    Pagination,
    SearchInput,
    StatusButtons,
    UserInformation
  }
})
export default class RewardPeriodDetailRequests extends Vue {
  private imageUrl = process.env.VUE_APP_HIVE_HOME_URL + '/images/';
  private readonly headerTable: TableHeaderColumns = [
    {
      value: 'user',
      text: 'User'
    },
    {
      value: 'item',
      text: 'Item'
    },
    {
      align: 'end',
      value: 'cost',
      text: 'Cost (Pollens)'
    },
    {
      align: 'end',
      value: 'estimatedPrice',
      text: 'Estimated Price (VND)'
    },
    {
      align: 'end',
      value: 'actualPrice',
      text: 'Actual Price (VND)'
    },
    {
      value: 'note',
      text: 'Note'
    },
    {
      align: 'center',
      value: 'invoiceImage',
      text: 'Invoice'
    },
    {
      value: 'createdAt',
      text: 'Created at'
    },
    {
      value: 'updatedAt',
      text: 'Resolved at'
    },
    {
      align: 'end',
      value: 'status',
      text: 'Status & actions'
    }
  ];
  private statusOptions: SelectionOptions = Object.values(REWARD_REQUEST_STATUSES).map(v => ({
    text: v.label,
    value: v.label
  }));

  private selectedStatus = [REWARD_REQUEST_STATUSES.PENDING.label];

  private loading = false;
  private rewardRequests: RewardRequestDTO[] = [];
  private displayRewardRequestsItem: RewardRequestDisplayDTO[] = [];
  private searchRequestByUsername = '';
  private pageCount = 0;
  private page = 1;
  private itemsPerPage = 10;

  private selectedRows: RewardRequestDisplayDTO[] = [];
  private rewardPeriodId = -1;
  private rejectDialogShown = false;
  private approveConfirmDialogShown = false;
  private requestStatus = REWARD_REQUEST_STATUSES;
  private updateStatusItem = {} as RewardRequestDisplayDTO;
  private inProgressDialogShown = false;

  customSort(items: RewardRequestDisplayDTO[], fields: string[], isDescArr: boolean[]): RewardRequestDisplayDTO[] {
    const field = fields[0];
    if (field) {
      const isDesc = isDescArr[0];
      switch (field) {
        case 'status': {
          return sortArrByRewardRequestStatus(items, field, isDesc);
        }
        case 'createdAt':
        case 'updatedAt': {
          return sortArrayByDateField(items, field, isDesc);
        }
        case 'user': {
          return sortArrByUserDTOField(items, field, isDesc);
        }
        case 'cost':
        case 'estimatedPrice':
        case 'actualPrice': {
          return sortArrByNumberField(items, field, isDesc);
        }
        case 'note': {
          return sortArrByTextField(items, field, isDesc);
        }
        default: {
          return items;
        }
      }
    }
    return items;
  }

  buildRewardRequestDisplayDTOs(rewardRequests: RewardRequestDTO[]): RewardRequestDisplayDTO[] {
    return rewardRequests.map(rewardRequest => ({
      id: rewardRequest.id,
      user: rewardRequest.user,
      rewardItemId: rewardRequest.rewardItem.id,
      prize: rewardRequest.rewardItem.prize,
      cost: rewardRequest.rewardItem.cost,
      estimatedPrice: rewardRequest.rewardItem.estimatedPrice,
      actualPrice: rewardRequest.actualPrice,
      createdAt: rewardRequest.createdAt,
      updatedAt: rewardRequest.updatedAt,
      status: rewardRequest.status,
      reason: rewardRequest.reason,
      note: rewardRequest.note,
      invoiceImage: rewardRequest.invoiceImage
    }));
  }

  get displayRewardRequests(): RewardRequestDisplayDTO[] {
    this.displayRewardRequestsItem = this.buildRewardRequestDisplayDTOs(this.rewardRequests);
    this.displayRewardRequestsItem = this.filterRewardRequestsByCreatedAt(this.displayRewardRequestsItem);
    this.displayRewardRequestsItem = this.filterRewardRequestByUsername(this.displayRewardRequestsItem);
    if (this.selectedStatus.length) {
      this.displayRewardRequestsItem = this.filterRewardRequestByStatus(this.displayRewardRequestsItem);
    }
    return this.displayRewardRequestsItem;
  }

  onSearch(searchText: string): void {
    this.searchRequestByUsername = searchText;
    this.selectedRows = [];
  }

  onSelectedStatusChange(selectedStatus: string[]): void {
    this.selectedStatus = selectedStatus;
    this.selectedRows = [];
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  displayDate(dateString: string): string {
    const fromDate = moment(dateString).format(DATE_FORMAT.SHORT);
    const toDate = moment(dateString).fromNow();
    return `${fromDate}<br /><i>${toDate}</i>`;
  }

  getPrizeType(type: string): string {
    return displayPrizeType(type);
  }

  isSelectStatusRejectedOrApproved(): boolean {
    return (
      this.selectedStatus.includes(REWARD_REQUEST_STATUSES.APPROVED.label) ||
      this.selectedStatus.includes(REWARD_REQUEST_STATUSES.REJECTED.label)
    );
  }

  isSelectStatusPending(): boolean {
    return this.selectedStatus.includes(REWARD_REQUEST_STATUSES.PENDING.label);
  }

  filterRewardRequestsByCreatedAt(rewardRequests: RewardRequestDisplayDTO[]): RewardRequestDisplayDTO[] {
    return sortArrayByDateField(rewardRequests || [], 'createdAt', false);
  }

  filterRewardRequestByUsername(rewardRequests: RewardRequestDisplayDTO[]): RewardRequestDisplayDTO[] {
    return !isEmpty(this.searchRequestByUsername)
      ? rewardRequests.filter(request => request.user.fullName.toLowerCase().includes(this.searchRequestByUsername))
      : rewardRequests;
  }

  filterRewardRequestByStatus(rewardRequests: RewardRequestDisplayDTO[]): RewardRequestDisplayDTO[] {
    return rewardRequests.filter(rewardRequest => this.selectedStatus.includes(rewardRequest.status || ''));
  }

  isDoneRequest(status: string): boolean {
    return status !== REWARD_REQUEST_STATUSES.PENDING.label;
  }

  async setInitialRewardRequests(): Promise<void> {
    const { data } = await fetchRewardRequestByPeriodId(this.rewardPeriodId);
    this.rewardRequests = data;
    this.selectedRows = [];
    this.updateStatusItem = {} as RewardRequestDisplayDTO;
  }

  async created(): Promise<void> {
    this.loading = true;
    this.rewardPeriodId = parseInt(this.$route.params.id);
    await this.setInitialRewardRequests();
    this.displayRewardRequestsItem = this.buildRewardRequestDisplayDTOs(this.rewardRequests);

    this.loading = false;
  }

  addNotification(type: string, title: string, content: string[], icon: string, multiline: boolean): void {
    const notification = { showing: true, type, title, content, icon, multiline, height: 0 };
    this.$store.commit('notification/setNotification', notification);
  }

  async approveRequest(): Promise<void> {
    const { data, dataUpdateRewardPeriodState } = this.getRequestData(REWARD_REQUEST_STATUSES.APPROVED.label);
    const updateRewardRequestResult = await updateRewardRequest(data);
    if (updateRewardRequestResult.data) {
      this.notifySuccessAndUpdateRequest(dataUpdateRewardPeriodState, NOTIFICATION.TEXT.approveRewardRequestSuccess);
    } else {
      this.notifyFailed(NOTIFICATION.TEXT.approveRewardRequestFailed);
    }
    this.setInitialRewardRequests();
  }

  openApproveDialog(item: RewardRequestDisplayDTO): void {
    this.updateStatusItem = item || ({} as RewardRequestDisplayDTO);
    this.approveConfirmDialogShown = true;
  }

  onApproveDialogConfirmed(actualPrice: number): void {
    if (this.updateStatusItem?.id) {
      this.updateStatusItem.actualPrice = actualPrice;
      this.approveRequest();
    }
    this.approveConfirmDialogShown = false;
  }

  openRejectDialog(item: RewardRequestDisplayDTO): void {
    this.rejectDialogShown = true;
    this.updateStatusItem = item;
  }

  openInProgressDialog(item: RewardRequestDisplayDTO): void {
    this.inProgressDialogShown = true;
    this.updateStatusItem = item;
  }

  private notifyFailed(failedText: string): void {
    this.addNotification(NOTIFICATION.TYPE.error, failedText, [], NOTIFICATION.ICON.error, false);
  }

  private notifySuccessAndUpdateRequest(data: RewardRequestDisplayDTO, successText: string): void {
    this.addNotification(NOTIFICATION.TYPE.success, successText, [], NOTIFICATION.ICON.success, false);
    this.$store.commit('rewardPeriod/updateRewardRequest', data);
  }

  private getRequestData(newStatus: string) {
    const result = {
      id: this.updateStatusItem.id,
      userId: this.updateStatusItem.user.id,
      status: newStatus,
      reason: this.updateStatusItem.reason,
      actualPrice: this.updateStatusItem.actualPrice ?? 0
    };
    return {
      data: [{ ...result, rewardItemId: this.updateStatusItem.rewardItemId }],
      dataUpdateRewardPeriodState: {
        ...result,
        user: this.updateStatusItem.user,
        prize: this.updateStatusItem.prize,
        cost: this.updateStatusItem.cost,
        estimatedPrice: this.updateStatusItem.estimatedPrice
      } as RewardRequestDisplayDTO
    };
  }

  async changeRequestStatusToInProgress(): Promise<void> {
    const { data, dataUpdateRewardPeriodState } = this.getRequestData(REWARD_REQUEST_STATUSES.IN_PROGRESS.label);
    const updateResult = await updateRewardRequest(data);
    if (updateResult.data) {
      this.notifySuccessAndUpdateRequest(dataUpdateRewardPeriodState, NOTIFICATION.TEXT.inProgressRewardRequestSuccess);
    } else {
      this.notifyFailed(NOTIFICATION.TEXT.inProgressRewardRequestFailed);
    }
    this.inProgressDialogShown = false;
    await this.setInitialRewardRequests();
  }

  async rejectRequest(): Promise<void> {
    const { data, dataUpdateRewardPeriodState } = this.getRequestData(REWARD_REQUEST_STATUSES.REJECTED.label);
    const updateResult = await updateRewardRequest(data);
    if (updateResult.data) {
      this.notifySuccessAndUpdateRequest(dataUpdateRewardPeriodState, NOTIFICATION.TEXT.rejectRewardRequestSuccess);
    } else {
      this.notifyFailed(NOTIFICATION.TEXT.rejectRewardRequestFailed);
    }
    this.rejectDialogShown = false;
    await this.setInitialRewardRequests();
  }

  get rewardPeriod(): RewardPeriodDTO {
    return this.$store.getters['rewardPeriod/rewardPeriod'];
  }

  formatCurrency(amount: number): string {
    return formatCurrency(amount);
  }

  displayActualPrice(item: RewardRequestDisplayDTO): DisplayActualPrice {
    if (isNull(item.actualPrice)) {
      return {
        class: 'grey--text text--lighten-4',
        text: 'pending...'
      };
    }
    return {
      class: '',
      text: formatCurrency(item.actualPrice as number)
    };
  }

  getDisplayNote(note: string): { phone: string; address: string } {
    const [phone, address] = note?.split('$') || ['', ''];
    return {
      phone,
      address
    };
  }

  handleDownloadInvoice(invoiceImagePath: string): void {
    const imageUrl = this.imageUrl + invoiceImagePath;
    saveAs(imageUrl, invoiceImagePath);
  }
}
